import { createListingPhotoEditor } from '../shared/listing-photo-editor';
import { initListingPhotoDropzone } from '../shared/listing-photo-dropzone';
import { prepareListingImage, DEFAULT_TARGET_BYTES } from '../shared/image-preparation-utils';

export function initInvitationPhotos(form) {
    const root = form.querySelector('[data-invitation-photo-editor]');
    if (!root) return;
    const input = root.querySelector('[data-invitation-photos]');
    const dropzone = root.querySelector('[data-invitation-dropzone]');
    const list = root.querySelector('[data-invitation-photo-list]');
    const status = root.querySelector('[data-upload-status]');
    const errorBox = root.querySelector('[data-photo-error]');
    const save = root.querySelector('[data-save-photos]');
    const publish = form.querySelector('[data-claim-submit]');
    const missing = form.querySelector('[data-photos-required]');
    const maxPhotos = Number(root.dataset.maxPhotos);
    const maxBytes = Number(root.dataset.maxBytes);
    const requestBytes = Number(root.dataset.requestBytes);
    let nextKey = 0;
    const identify = photos => photos.map(photo => ({ ...photo, key: `photo:${++nextKey}`, label: photo.file ? 'do zapisania' : 'zapisane' }));
    let photos = identify(JSON.parse(root.dataset.photos || '[]'));
    let dirty = false, busy = false;
    const editor = createListingPhotoEditor({
        list, modal: document.querySelector('[data-plf-photo-lightbox]'),
        onRemove: photo => {
            if (busy) return;
            if (photo.file) URL.revokeObjectURL(photo.url);
            photos = photos.filter(item => item.key !== photo.key);
            markDirty();
        },
        onChange: items => { photos = items; markDirty(); },
    });
    function error(message = '') { errorBox.textContent = message; errorBox.hidden = !message; }
    function updateState() {
        save.disabled = busy || !dirty;
        input.disabled = busy;
        publish.disabled = publish.dataset.blocked === '1' || busy || dirty || !photos.length;
        missing.hidden = photos.length > 0 && !dirty;
        missing.textContent = dirty ? 'Zapisz zmiany w zdjęciach przed wysłaniem linku potwierdzającego.' : 'Dodaj i zapisz co najmniej jedno zdjęcie w sekcji „Zdjęcia”.';
        root.setAttribute('aria-busy', String(busy));
    }
    function markDirty() { dirty = true; status.textContent = 'Masz niezapisane zmiany w zdjęciach.'; render(); }
    function render() {
        editor.setItems(photos, false);
        editor.setBusy(busy);
        updateState();
    }
    async function addFiles(files) {
        if (busy || !files.length) return;
        error();
        if (photos.length + files.length > maxPhotos) { error(`Możesz dodać maksymalnie ${maxPhotos} zdjęć. Usuń część obecnych zdjęć lub wybierz mniej plików.`); return; }
        busy = true; status.textContent = 'Przygotowuję zdjęcia…'; render();
        const prepared = [];
        try {
            for (const file of files) {
                if (file.size > Math.max(maxBytes, 30 * 1048576)) throw new Error(`Plik „${file.name}” jest zbyt duży. Wybierz plik do 30 MB.`);
                const entry = await prepareListingImage(file, {uploadMaxBytes:maxBytes, targetBytes:Math.min(DEFAULT_TARGET_BYTES, maxBytes || DEFAULT_TARGET_BYTES)});
                prepared.push({file:entry.file, url:entry.previewUrl});
            }
            photos.push(...identify(prepared)); dirty = true;
            status.textContent = 'Zdjęcia są gotowe. Sprawdź kolejność i zapisz zestaw.';
        } catch (failure) {
            prepared.forEach(photo => URL.revokeObjectURL(photo.url)); error(failure.message || 'Nie udało się przygotować zdjęć.'); status.textContent = 'Zdjęcia nie zostały dodane.';
        } finally { busy = false; input.value = ''; render(); }
    }
    initListingPhotoDropzone({ input, dropzone, addFiles, isBusy: () => busy });
    async function savePhotos() {
        if (busy || !dirty) return;
        error();
        const body = new FormData();
        const csrf = document.querySelector('meta[name=csrf-param]')?.content;
        if (csrf) body.set(csrf, document.querySelector('meta[name=csrf-token]').content);
        body.set('operation','photos'); body.set('version',form.elements.namedItem('version').value);
        let uploadIndex = 0, total = 0;
        const order = photos.map(photo => {
            if (!photo.file) return {existing:photo.existing};
            body.append('photos[]',photo.file); total += photo.file.size; return {upload:uploadIndex++};
        });
        if (requestBytes > 0 && total > requestBytes * 0.9) { error('Ten zestaw jest zbyt duży do jednorazowego wysłania. Zapisz mniej zdjęć, a pozostałe dodaj w kolejnej partii.'); return; }
        body.set('photo_order',JSON.stringify(order));
        busy = true; status.textContent = 'Zapisuję zdjęcia…'; render();
        try {
            const data = await new Promise((resolve, reject) => {
                const request = new XMLHttpRequest(); request.open('POST',form.action); request.setRequestHeader('X-Requested-With','XMLHttpRequest'); request.timeout = 120000;
                request.upload.onprogress = event => { if (event.lengthComputable) status.textContent = `Wysyłanie zdjęć: ${Math.round(event.loaded / event.total * 100)}%`; };
                request.onerror = () => reject(new Error('Nie udało się połączyć z serwerem. Zdjęcia pozostają w formularzu — spróbuj ponownie.'));
                request.ontimeout = () => reject(new Error('Serwer nie potwierdził zapisu. Odśwież podgląd, aby sprawdzić, czy zdjęcia zostały zapisane.'));
                request.onload = () => {
                    let payload; try { payload = JSON.parse(request.responseText); } catch (_) {}
                    if (request.status < 200 || request.status >= 300 || !payload?.ok) reject(new Error(payload?.message || (request.status === 413 ? 'Przekroczono limit wysyłki. Wybierz mniej zdjęć.' : 'Nie udało się zapisać zdjęć. Odśwież podgląd lub spróbuj ponownie.')));
                    else resolve(payload);
                };
                request.send(body);
            });
            photos.forEach(photo => { if (photo.file) URL.revokeObjectURL(photo.url); });
            photos = identify(data.photos); form.elements.namedItem('version').value = data.version; dirty = false;
            status.textContent = 'Zdjęcia zostały zapisane.';
            document.querySelector('[data-claim-sent]')?.remove();
        } catch (failure) { error(failure.message); status.textContent = 'Nie potwierdzono zapisu zdjęć.'; }
        finally { busy = false; render(); }
    }
    form.addEventListener('submit', event => {
        if (event.submitter?.value === 'photos') { event.preventDefault(); savePhotos(); }
        else if (busy || dirty) { event.preventDefault(); error('Najpierw zapisz zdjęcia.'); root.scrollIntoView({block:'start',behavior:'smooth'}); }
    });
    window.addEventListener('beforeunload', event => { if (dirty || busy) { event.preventDefault(); event.returnValue = ''; } });
    render();
}
